/**
 * LRU eviction for the per-conversation message buffer (`convMessagesRef`) in
 * MainOfficeView (F-5.5).
 *
 * The buffer keeps an in-flight conversation's optimistic + streaming messages
 * alive across navigation (the "no ghosting" guarantee). It was never pruned —
 * not even on conversation delete — so it grew unboundedly for the session. These
 * helpers cap it while NEVER evicting a conversation that is:
 *   - currently on screen (the active id), or
 *   - still processing a turn (its messages aren't committed to the DB yet).
 *
 * `order` is a most-recently-touched-LAST array of conversation ids used purely to
 * decide eviction victims; the actual buffer is a plain object keyed by id.
 */

export const DEFAULT_BUFFER_CAP = 10;

/**
 * Record that a conversation was just touched (seeded/read). Returns a NEW order
 * array with `convId` moved to the end (most-recent). Pure — does not mutate input.
 *
 * @param {string[]} order  prior touch order (oldest first)
 * @param {string} convId   the conversation just accessed
 * @returns {string[]} new order array
 */
export const touchConvOrder = (order, convId) => {
    if (!convId) return order.slice();
    return [...order.filter((id) => id !== convId), convId];
};

/**
 * Decide which conversation buffers to evict.
 *
 * Keeps at most `cap` "evictable" conversations (those NOT protected). Protected
 * ids (active + processing) are never counted against the cap and never evicted.
 * Evicts the OLDEST evictable conversations first (front of `order`).
 *
 * @param {string[]} order        touch order, oldest first
 * @param {Set<string>} protectedIds  ids that must never be evicted (active + processing)
 * @param {number} cap            max number of NON-protected buffers to retain
 * @returns {string[]} ids to evict (subset of `order`)
 */
export const selectEvictions = (order, protectedIds = new Set(), cap = DEFAULT_BUFFER_CAP) => {
    const evictable = order.filter((id) => !protectedIds.has(id));
    const overflow = evictable.length - cap;
    if (overflow <= 0) return [];
    // The oldest `overflow` evictable ids (front of the list) are the victims.
    return evictable.slice(0, overflow);
};

/**
 * Prune the buffer map + order array IN PLACE-style but returning new values.
 *
 * Removes any evicted ids from both `bufferMap` (mutated — it's a ref.current
 * object) and returns a pruned order array. Also drops order entries that no
 * longer exist in the map (defensive).
 *
 * @param {Object} bufferMap        the `convMessagesRef.current` object (mutated)
 * @param {string[]} order          touch order, oldest first
 * @param {Set<string>} protectedIds  ids that must never be evicted
 * @param {number} cap              max non-protected buffers
 * @returns {{ order: string[], evicted: string[] }}
 */
export const pruneConvBuffers = (bufferMap, order, protectedIds = new Set(), cap = DEFAULT_BUFFER_CAP) => {
    const evicted = selectEvictions(order, protectedIds, cap);
    for (const id of evicted) {
        delete bufferMap[id];
    }
    const evictedSet = new Set(evicted);
    const nextOrder = order.filter((id) => !evictedSet.has(id) && (id in bufferMap));
    return { order: nextOrder, evicted };
};

/**
 * Delete a single conversation's buffer entry (on conversation delete) from both
 * the map and the order array. Returns the pruned order array.
 *
 * @param {Object} bufferMap  the `convMessagesRef.current` object (mutated)
 * @param {string[]} order    touch order
 * @param {string} convId     conversation being deleted
 * @returns {string[]} new order array
 */
export const removeConvBuffer = (bufferMap, order, convId) => {
    if (convId in bufferMap) delete bufferMap[convId];
    return order.filter((id) => id !== convId);
};
